import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send, User, MessageSquare, ArrowLeft } from 'lucide-react';
import { useForm, ValidationError } from '@formspree/react';

const Contact = ({ language = 'de', setCurrentPage }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [state, handleSubmit] = useForm("mbdjryvk");
  const isSubmitting = state.submitting;
  const submitStatus = state.succeeded ? 'success' : (state.errors && state.errors.length > 0 ? 'error' : '');

  const translations = {
    de: {
      title: 'Kontaktieren Sie uns',
      subtitle: 'Wir sind hier, um Ihnen zu helfen',
      description: 'Haben Sie Fragen zu unserem Hotel oder Restaurant? Zögern Sie nicht, uns zu kontaktieren. Wir freuen uns darauf, von Ihnen zu hören.',
      
      form: {
        name: 'Vollständiger Name',
        namePlaceholder: 'Ihr Name',
        email: 'E-Mail-Adresse',
        emailPlaceholder: 'ihre.email@example.com',
        phone: 'Telefonnummer',
        phonePlaceholder: '+49 123 456 7890',
        subject: 'Betreff',
        subjectPlaceholder: 'Worum geht es?',
        message: 'Nachricht',
        messagePlaceholder: 'Schreiben Sie uns Ihre Nachricht...',
        submit: 'Nachricht senden',
        sending: 'Wird gesendet...'
      },

      contact: {
        title: 'Kontakt Informationen',
        phone: 'Telefon',
        email: 'E-Mail',
        address: 'Adresse',
        hours: 'Öffnungszeiten'
      },

      info: {
        phone: '+49232456650',
        email: 'info@lacasa-hotel.de',
        address: 'Wodantal 41, 45529 Hattingen, Deutschland',
        hours: 'Restaurant: Fr-Di 17:00-22:00 | Hotel Check-in: Fr-Di 15:00-18:00'
      },

      success: 'Nachricht erfolgreich gesendet!',
      error: 'Fehler beim Senden der Nachricht. Bitte versuchen Sie es erneut.',
      back: 'Zurück zur Startseite'
    },
    en: {
      title: 'Contact Us',
      subtitle: 'We\'re here to help you',
      description: 'Have questions about our hotel or restaurant? Don\'t hesitate to contact us. We look forward to hearing from you.',
      
      form: {
        name: 'Full Name',
        namePlaceholder: 'Your Name',
        email: 'Email Address',
        emailPlaceholder: 'your.email@example.com',
        phone: 'Phone Number',
        phonePlaceholder: '+49 123 456 7890',
        subject: 'Subject',
        subjectPlaceholder: 'What is this about?',
        message: 'Message',
        messagePlaceholder: 'Write your message here...',
        submit: 'Send Message',
        sending: 'Sending...'
      },

      contact: {
        title: 'Contact Information',
        phone: 'Phone',
        email: 'Email',
        address: 'Address',
        hours: 'Opening Hours'
      },

      info: {
        phone: '+49232456650',
        email: 'info@lacasa-hotel.de',
        address: 'Wodantal 41, 45529 Hattingen, Germany',
        hours: 'Restaurant: Fri-Tue 5:00 PM-10:00 PM | Hotel Check-in: Fri-Tue 3:00 PM-6:00 PM'
      },

      success: 'Message sent successfully!',
      error: 'Error sending message. Please try again.',
      back: 'Back to Homepage'
    }
  };

  const t = translations[language];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Reset form on successful submission
  if (state.succeeded) {
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    }, 3000);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-luxury-charcoal via-luxury-slate to-luxury-charcoal pt-8 pb-12">
      
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 pt-4">
        <button
          onClick={() => setCurrentPage && setCurrentPage('home')}
          className="flex items-center text-luxury-gold hover:text-white transition-colors duration-300 mb-8"
        >
          <ArrowLeft className="mr-2" size={20} />
          <span className="font-semibold">{t.back}</span>
        </button>
      </div>
      
      {/* Header Section */}
      <div className="text-center py-8 px-4">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
          {t.title}
        </h1>
        <p className="text-xl text-luxury-gold mb-4 max-w-2xl mx-auto">
          {t.subtitle}
        </p>
        <p className="text-gray-300 text-lg max-w-3xl mx-auto leading-relaxed">
          {t.description}
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-12">
        
        {/* Contact Form */}
        <div
          className="bg-luxury-charcoal/80 backdrop-blur-lg rounded-3xl p-8 border border-luxury-gold/20 shadow-2xl"
        >
          <div className="flex items-center mb-6">
            <MessageSquare className="text-luxury-gold mr-3" size={24} />
            <h2 className="text-2xl font-bold text-white">
              {t.form.submit}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Hidden field for custom email subject */}
            <input type="hidden" name="_subject" value="Neue Nachricht von La Casa Hotel Website" />
            
            {/* Name Field */}
            <div>
              <label className="block text-luxury-gold font-semibold mb-2">
                {t.form.name}
              </label>
              <div className="relative">
                <User className="absolute left-3 top-3 text-luxury-gold/60" size={20} />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t.form.namePlaceholder}
                  required
                  className="w-full pl-12 pr-4 py-3 bg-luxury-slate/50 border border-luxury-gold/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-luxury-gold focus:ring-2 focus:ring-luxury-gold/20 transition-all"
                />
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-luxury-gold font-semibold mb-2">
                {t.form.email}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 text-luxury-gold/60" size={20} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t.form.emailPlaceholder}
                  required
                  className="w-full pl-12 pr-4 py-3 bg-luxury-slate/50 border border-luxury-gold/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-luxury-gold focus:ring-2 focus:ring-luxury-gold/20 transition-all"
                />
                <ValidationError 
                  prefix="Email" 
                  field="email"
                  errors={state.errors}
                  className="text-red-400 text-sm mt-1"
                />
              </div>
            </div>

            {/* Phone Field */}
            <div>
              <label className="block text-luxury-gold font-semibold mb-2">
                {t.form.phone}
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 text-luxury-gold/60" size={20} />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder={t.form.phonePlaceholder}
                  className="w-full pl-12 pr-4 py-3 bg-luxury-slate/50 border border-luxury-gold/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-luxury-gold focus:ring-2 focus:ring-luxury-gold/20 transition-all"
                />
              </div>
            </div>

            {/* Subject Field */}
            <div>
              <label className="block text-luxury-gold font-semibold mb-2">
                {t.form.subject}
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder={t.form.subjectPlaceholder}
                required
                className="w-full px-4 py-3 bg-luxury-slate/50 border border-luxury-gold/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-luxury-gold focus:ring-2 focus:ring-luxury-gold/20 transition-all"
              />
            </div>

            {/* Message Field */}
            <div>
              <label className="block text-luxury-gold font-semibold mb-2">
                {t.form.message}
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder={t.form.messagePlaceholder}
                required
                rows="5"
                className="w-full px-4 py-3 bg-luxury-slate/50 border border-luxury-gold/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-luxury-gold focus:ring-2 focus:ring-luxury-gold/20 transition-all resize-none"
              />
              <ValidationError 
                prefix="Message" 
                field="message"
                errors={state.errors}
                className="text-red-400 text-sm mt-1"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-luxury-gold to-luxury-darkGold hover:from-luxury-darkGold hover:to-luxury-gold text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t.form.sending}
                </div>
              ) : (
                <div className="flex items-center">
                  <Send className="mr-2" size={20} />
                  {t.form.submit}
                </div>
              )}
            </button>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-500/20 border-2 border-green-500/60 text-green-400 px-4 py-4 rounded-xl flex items-center gap-3 shadow-lg"
              >
                <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="font-semibold">{t.success}</p>
              </motion.div>
            )}
            
            {submitStatus === 'error' && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-500/20 border-2 border-red-500/60 text-red-400 px-4 py-4 rounded-xl flex items-center gap-3 shadow-lg"
              >
                <div className="flex-shrink-0 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <p className="font-semibold">{t.error}</p>
              </motion.div>
            )}
          </form>
        </div>

        {/* Contact Information */}
        <motion.div
          className="space-y-8"
        >
          <div className="bg-luxury-charcoal/80 backdrop-blur-lg rounded-3xl p-8 border border-luxury-gold/20 shadow-2xl">
            <h2 className="text-2xl font-bold text-white mb-6">
              {t.contact.title}
            </h2>
            
            <div className="space-y-6">
              {/* Phone */}
              <div className="flex items-center p-4 bg-luxury-slate/30 rounded-xl border border-luxury-gold/10">
                <Phone className="text-luxury-gold mr-4" size={24} />
                <div>
                  <p className="text-luxury-gold font-semibold">{t.contact.phone}</p>
                  <p className="text-white">{t.info.phone}</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-center p-4 bg-luxury-slate/30 rounded-xl border border-luxury-gold/10">
                <Mail className="text-luxury-gold mr-4" size={24} />
                <div>
                  <p className="text-luxury-gold font-semibold">{t.contact.email}</p>
                  <p className="text-white">{t.info.email}</p>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-center p-4 bg-luxury-slate/30 rounded-xl border border-luxury-gold/10">
                <MapPin className="text-luxury-gold mr-4" size={24} />
                <div>
                  <p className="text-luxury-gold font-semibold">{t.contact.address}</p>
                  <p className="text-white">{t.info.address}</p>
                </div>
              </div>

              {/* Hours */}
              <div className="flex items-center p-4 bg-luxury-slate/30 rounded-xl border border-luxury-gold/10">
                <Clock className="text-luxury-gold mr-4" size={24} />
                <div>
                  <p className="text-luxury-gold font-semibold">{t.contact.hours}</p>
                  <p className="text-white">{t.info.hours}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Google Maps */}
          <div className="bg-luxury-charcoal/80 backdrop-blur-lg rounded-3xl p-8 border border-luxury-gold/20 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-4">
              {language === 'de' ? 'Unsere Lage' : 'Our Location'}
            </h3>
            <div className="rounded-xl overflow-hidden border border-luxury-gold/20">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2481.743920826595!2d7.183644315678234!3d51.40112797962189!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b8e1c3e3e3e3e3%3A0x0!2sWodantal%2041%2C%2045529%20Hattingen%2C%20Germany!5e0!3m2!1sen!2sde!4v1620000000000!5m2!1sen!2sde"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-[300px]"
              />
            </div>
            <div className="mt-4 text-center">
              <p className="text-gray-300 text-sm">
                {language === 'de' 
                  ? 'Haus Niggemann, Hattingen - Im Herzen des Ruhrgebiets' 
                  : 'Haus Niggemann, Hattingen - In the heart of the Ruhr area'}
              </p>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default Contact;
